import { useEffect, useLayoutEffect, useState } from "react";
import React from "react";
import { LoadingFullPage } from "components/Loading/FullPage";
import { config } from "config";
import router from "@pankod/refine-react-router-v6";
import { getToken } from "auth/azure";
import posthog from "lib/posthog";

const oauthCodesInFlight = new Set<string>();

const isOAuthCallbackPath = (path: string) =>
  /^\/auth\/[^/]+\/callback/.test(path);

const buildOAuthCallbackQuery = (search: string) => {
  const params = new URLSearchParams(search);
  const code = params.get("code");
  if (!code) return null;

  const state = params.get("state");
  let query = `?code=${encodeURIComponent(code)}`;
  if (state) {
    query += `&state=${encodeURIComponent(state)}`;
  }
  return query;
};

const redirectToApp = () => {
  let redirectPath = localStorage.getItem("redirectPath");
  localStorage.removeItem("redirectPath");

  if (!redirectPath || isOAuthCallbackPath(redirectPath.split("#")[0])) {
    redirectPath = "/";
  }

  window.location.replace(redirectPath);
};

const handleLoginSuccess = (res: {
  tokens: unknown;
  user?: { id: string | number; email?: string };
}) => {
  localStorage.setItem(config.TOKEN_KEY, JSON.stringify(res.tokens));
  if (res.user) {
    localStorage.setItem("user-health", JSON.stringify(res.user));
    try {
      posthog.identify(String(res.user.id), {
        email: res.user.email,
      });
    } catch {
      // PostHog should not block login redirect
    }
  }
  redirectToApp();
};

const exchangeOAuthCode = (
  provider: string,
  search: string,
  fetchOptions?: RequestInit,
) => {
  const callbackQuery = buildOAuthCallbackQuery(search);
  if (!callbackQuery) {
    return Promise.reject(new Error("Missing authorization code."));
  }

  const exchangeKey = `${provider}:${callbackQuery}`;
  if (oauthCodesInFlight.has(exchangeKey)) {
    if (localStorage.getItem(config.TOKEN_KEY)) {
      redirectToApp();
    }
    return Promise.resolve();
  }
  oauthCodesInFlight.add(exchangeKey);

  return fetch(
    `${config.API_URL}/auth/${provider}/redirect${callbackQuery}`,
    fetchOptions,
  )
    .then((res) => {
      if (res.status !== 200) {
        throw new Error(`Couldn't login to Service. Status: ${res.status}`);
      }
      return res;
    })
    .then((res) => res.json())
    .then(handleLoginSuccess)
    .catch((error) => {
      oauthCodesInFlight.delete(exchangeKey);
      throw error;
    });
};

const parseGoogleCallbackHash = () => {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return null;

  const userMarker = "&user=";
  const userIndex = hash.indexOf(userMarker);
  const tokensPart =
    userIndex >= 0 ? hash.slice(0, userIndex) : hash;
  const userPart =
    userIndex >= 0 ? hash.slice(userIndex + userMarker.length) : null;

  if (!tokensPart.startsWith("tokens=")) return null;

  const tokensRaw = decodeURIComponent(tokensPart.slice("tokens=".length));
  const userRaw = userPart ? decodeURIComponent(userPart) : null;

  return {
    tokens: JSON.parse(tokensRaw),
    user: userRaw ? JSON.parse(userRaw) : undefined,
  };
};

const exchangeMicrosoftToken = (idToken: string) => {
  const exchangeKey = `microsoft:${idToken.slice(0, 32)}`;
  if (oauthCodesInFlight.has(exchangeKey)) {
    if (localStorage.getItem(config.TOKEN_KEY)) {
      redirectToApp();
    }
    return Promise.resolve();
  }
  oauthCodesInFlight.add(exchangeKey);

  return fetch(`${config.API_URL}/auth/microsoft/redirect`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })
    .then((res) => {
      if (res.status !== 200) {
        throw new Error(`Couldn't login to Service. Status: ${res.status}`);
      }
      return res;
    })
    .then((res) => res.json())
    .then(handleLoginSuccess)
    .catch((error) => {
      oauthCodesInFlight.delete(exchangeKey);
      throw error;
    });
};

const LoginRedirect = () => {
  const [text, setText] = useState("Loading...");
  const pathname = window.location.pathname;
  const provider = pathname.split("/")[2];
  const location = router.useLocation();

  useLayoutEffect(() => {
    if (provider !== "google") return;

    localStorage.removeItem("redirectPath");

    try {
      const authData = parseGoogleCallbackHash();
      if (!authData) {
        setText("Google login failed. Please try again.");
        return;
      }

      handleLoginSuccess(authData);
    } catch {
      setText("Google login failed. Please try again.");
    }
  }, [provider]);

  useEffect(() => {
    if (provider !== "microsoft") return;

    localStorage.removeItem("redirectPath");

    getToken()
      .then((token) => exchangeMicrosoftToken(token))
      .catch(() => {
        setText("Microsoft login failed. Please try again.");
      });
  }, [provider]);

  useEffect(() => {
    if (provider === "microsoft" || provider === "google") return;

    exchangeOAuthCode(provider, location.search).catch(() => {
      setText("An error occurred, please see the developer console.");
    });
  }, [location.search, provider]);

  return <LoadingFullPage text={text} />;
};

export default LoginRedirect;
